import {
  Alert,
  Button,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { type OrphanSubject, PLATFORM_TENANT, type PlatformTenant, platform } from "../../data/api";
import { useAction, useLoad } from "../../data/data";
import { StatusBadge } from "../../ui/components/StatusBadge";
import {
  Failure,
  Field,
  FormRow,
  Hint,
  Loading,
  Mono,
  PageHeader,
  Section,
} from "../../ui/components/primitives";

// ---
// Platform plane
//
// Creating tenants and appointing each tenant's first administrator. Deliberately narrow: the
// platform admin does not administer inside a tenant, because once a tenant has an admin that
// admin manages their own. Keeping the role to bootstrap keeps its blast radius small.
// ---

export function Platform({
  token,
  subjectId,
}: {
  token: string | null;
  /** The signed-in subject, so the first self-appointment needs no copying between pages. */
  subjectId: string;
}): React.JSX.Element {
  // No platform token means nothing to ask for; the page says so below instead.
  const tenants = useLoad(
    async () => (token === null ? [] : await platform.tenants(token)),
    [token],
  );
  const orphans = useLoad(
    async () => (token === null ? [] : await platform.orphanedSubjects(token)),
    [token],
  );
  const action = useAction(() => {
    tenants.reload();
    orphans.reload();
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [id, setId] = useState("");
  const [appoint, setAppoint] = useState({
    tenantId: "",
    subjectId: "",
    roleKey: "iam.tenant_admin",
  });
  const [erase, setErase] = useState("");
  // What the Check came back with. `null` means not asked yet; `found: false` is a name that
  // matches no account, which must not be erasable.
  const [resolved, setResolved] = useState<{
    found: boolean;
    subjectId: string | null;
    label: string;
    tenants: string[];
  } | null>(null);

  if (token === null) {
    return (
      <>
        <PageHeader title="Tenants" />
        <Section title="Not authorized">
          <Hint sx={{ mt: 0 }}>
            The platform plane needs a token for{" "}
            <Mono>https://auth.flows.aiui.run/platform-api</Mono>, held only by members of the
            reserved <Mono>{PLATFORM_TENANT}</Mono> tenant. Sign in as the seeded platform
            administrator, or have one appoint you.
          </Hint>
        </Section>
      </>
    );
  }

  const active = token;

  function act(fn: () => Promise<unknown>, message: string): void {
    setNotice(null);
    action.run(async () => {
      await fn();
      setNotice(message);
    });
  }

  return (
    <>
      <PageHeader title="Tenants" />

      {notice !== null && (
        <Alert severity="success" sx={{ mb: 4 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Section title="Create a tenant">
        <FormRow>
          <Field label="Tenant id" sx={{ flex: "1 1 240px", maxWidth: 320 }}>
            <TextField
              size="small"
              value={id}
              onChange={(event) => setId(event.target.value)}
              placeholder="acme"
            />
          </Field>
          <Button
            variant="contained"
            disabled={id === "" || action.busy}
            onClick={() =>
              act(
                () => platform.createTenant(active, { id, name: id }).then(() => setId("")),
                `Created ${id}. You were appointed its administrator, so it is now in the switcher.`,
              )
            }
          >
            Create
          </Button>
        </FormRow>
        <Hint>A lower-kebab slug.</Hint>
      </Section>

      <Section title="Appoint an administrator">
        <Hint sx={{ mt: 0, mb: 4 }}>
          The one write that crosses a tenant boundary: no role inside a tenant can grant its own
          peer, so a tenant's first administrator is appointed from here.
        </Hint>
        <FormRow>
          <Field label="Tenant" sx={{ flex: "1 1 150px" }}>
            <Select
              size="small"
              displayEmpty
              value={appoint.tenantId}
              onChange={(event) => setAppoint({ ...appoint, tenantId: event.target.value })}
            >
              <MenuItem value="" disabled>
                Choose a tenant
              </MenuItem>
              {tenants.data?.map((tenant: PlatformTenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.id}
                </MenuItem>
              ))}
            </Select>
          </Field>
          <Field label="Subject id" sx={{ flex: "2 1 260px" }}>
            <TextField
              size="small"
              value={appoint.subjectId}
              onChange={(event) => setAppoint({ ...appoint, subjectId: event.target.value })}
              InputProps={{
                endAdornment: (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setAppoint({ ...appoint, subjectId })}
                    sx={{ mr: -2 }}
                  >
                    Me
                  </Button>
                ),
              }}
            />
          </Field>
          <Field label="Role" sx={{ flex: "1 1 180px" }}>
            <TextField
              size="small"
              value={appoint.roleKey}
              onChange={(event) => setAppoint({ ...appoint, roleKey: event.target.value })}
            />
          </Field>
          <Button
            variant="contained"
            disabled={appoint.tenantId === "" || appoint.subjectId === "" || action.busy}
            onClick={() =>
              act(
                () => platform.appoint(active, appoint),
                `Appointed ${appoint.roleKey} in ${appoint.tenantId}`,
              )
            }
          >
            Appoint
          </Button>
        </FormRow>
        <Hint>
          Machine roles are never appointable, and a role owned by one tenant cannot be granted
          inside another.
        </Hint>
      </Section>

      <Section title="Erase an identity">
        <Hint sx={{ mt: 0, mb: 4 }}>
          A subject is global: one account can hold roles in several tenants, so deleting it is a
          platform decision rather than any one tenant's. Each tenant removes the person from itself
          first, on its own User management page; only then is there nothing left to erase.
        </Hint>
        {orphans.data !== null && orphans.data.length > 0 && (
          <Table size="small" sx={{ mb: 5 }}>
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {orphans.data.map((orphan: OrphanSubject) => (
                <TableRow key={orphan.subjectId} hover>
                  <TableCell>{orphan.username ?? orphan.email ?? "(no account)"}</TableCell>
                  <TableCell>
                    <Mono sx={{ fontSize: 11 }}>{orphan.subjectId}</Mono>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      onClick={() =>
                        act(
                          () => platform.deleteSubject(active, orphan.subjectId),
                          `Erased ${orphan.username ?? orphan.email ?? orphan.subjectId}`,
                        )
                      }
                    >
                      Erase
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {orphans.data !== null && orphans.data.length === 0 && (
          <Hint sx={{ mt: 0, mb: 4 }}>Every account holds a role somewhere. Nothing to erase.</Hint>
        )}

        <FormRow>
          <Field label="Account" sx={{ flex: "2 1 300px" }}>
            <TextField
              size="small"
              value={erase}
              placeholder="Subject id, username or email"
              onChange={(event) => {
                setErase(event.target.value);
                setResolved(null);
              }}
            />
          </Field>
          <Button
            variant="text"
            disabled={erase === "" || action.busy}
            onClick={() => {
              setResolved(null);
              action.run(async () => {
                const result = await platform.subjectHoldings(active, erase);
                // Narrowed on `found`: a name matching no account carries no username or
                // email, so what was typed is the only label there is.
                setResolved({
                  found: result.found,
                  subjectId: result.subjectId,
                  label: result.found
                    ? (result.username ?? result.email ?? result.subjectId)
                    : erase,
                  tenants: [...result.tenants],
                });
              });
            }}
          >
            Check
          </Button>
          <Button
            variant="contained"
            color="error"
            // Enabled only for an account that exists and that no tenant holds. Both halves
            // matter: a name matching nothing held nothing either, so the form used to say "held
            // by no tenant" about a typo and then fail with "Unknown subject" when pressed.
            disabled={
              resolved === null || !resolved.found || resolved.tenants.length > 0 || action.busy
            }
            onClick={() => {
              const id = resolved?.subjectId;
              const label = resolved?.label ?? "";
              if (id === undefined || id === null) return;
              setErase("");
              setResolved(null);
              act(() => platform.deleteSubject(active, id), `Erased ${label}`);
            }}
          >
            Erase
          </Button>
        </FormRow>
        {resolved !== null &&
          (!resolved.found ? (
            <Hint>No account matches that id, username or email.</Hint>
          ) : resolved.tenants.length === 0 ? (
            <Hint>
              <Mono>{resolved.label}</Mono> is held by no tenant. Erasing removes the account and
              every credential on it.
            </Hint>
          ) : (
            <Hint>
              <Mono>{resolved.label}</Mono> is still held by{" "}
              <Mono>{resolved.tenants.join(", ")}</Mono>. Each of those must remove this person
              before the identity can go.
            </Hint>
          ))}
      </Section>

      <Failure error={action.error} />

      <Section flush title={`Tenants · ${tenants.data?.length ?? 0}`} sx={{ mt: 4 }}>
        <Failure error={tenants.error} />
        {tenants.pending ? (
          <Loading what="tenants" />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Projects</TableCell>
                <TableCell align="right">Members</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.data?.map((tenant: PlatformTenant) => (
                <TableRow key={tenant.id} hover>
                  <TableCell>
                    <Mono>{tenant.id}</Mono>
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      label={tenant.status}
                      tone={tenant.status === "active" ? "success" : "warning"}
                    />
                  </TableCell>
                  <TableCell align="right">{tenant.projects}</TableCell>
                  <TableCell align="right">{tenant.members}</TableCell>
                  <TableCell align="right">
                    {tenant.id !== PLATFORM_TENANT && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() =>
                          act(
                            () =>
                              platform.setTenantStatus(
                                active,
                                tenant.id,
                                tenant.status === "active" ? "suspended" : "active",
                              ),
                            `${tenant.status === "active" ? "Suspended" : "Reactivated"} ${tenant.id}`,
                          )
                        }
                      >
                        {tenant.status === "active" ? "Suspend" : "Reactivate"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </>
  );
}
